import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './LandingPage.css';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [openFAQ, setOpenFAQ] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [latestNews, setLatestNews] = useState([]);
  const [isNewsLoading, setIsNewsLoading] = useState(true);
  const [newsError, setNewsError] = useState('');

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  // Simulate loading effect
  useEffect(() => {
    const loadingTimer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);
    
    return () => clearTimeout(loadingTimer);
  }, []);

  useEffect(() => {
    const fetchLatestNews = async () => {
      try {
        setIsNewsLoading(true);
        setNewsError('');

        const response = await fetch('/api/news?limit=6');
        const data = await response.json();

        if (!response.ok) {
          setNewsError(data.message || 'Unable to load the latest news.');
          return;
        }

        setLatestNews(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching latest news:', error);
        setNewsError('Unable to load the latest news.');
      } finally {
        setIsNewsLoading(false);
      }
    };

    fetchLatestNews();
  }, []);

  const formatNewsDate = (value) => {
    if (!value) {
      return '';
    }

    return new Date(value).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const buildExcerpt = (content) => {
    if (!content) {
      return 'Read this article for practical driving knowledge and exam-ready guidance.';
    }

    const plain = content
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return plain.length > 155 ? `${plain.slice(0, 152)}...` : plain;
  };

  // Banner background styling
  const bannerUrl = `${process.env.PUBLIC_URL || ''}/assets/banner.png`;
  const heroImageUrl = `${process.env.PUBLIC_URL || ''}/assets/driving-licese-class7.png`;
  const qrCodeUrl = `${process.env.PUBLIC_URL || ''}/assets/qrcode.png`;
  const googlePlayBadgeUrl = `${process.env.PUBLIC_URL || ''}/assets/ongoogleplay.png`;
  const googlePlayAppUrl = 'https://play.google.com/store/apps/details?id=com.codewithnguyen.todoalbertadriving';
  const landingRootStyle = {
    backgroundImage: `linear-gradient(180deg, rgba(14, 22, 33, 0.45), rgba(14, 22, 33, 0.35)), url('${bannerUrl}')`,
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: 'fixed',
  };


  return (
    <div className="landing-root" style={landingRootStyle}>
      {/* Loading Screen */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <h2 className="loading-title">Alberta Class 7 Test Prep</h2>
            <p className="loading-subtitle">Driving Preparation App</p>
            <p className="loading-text">Preparing your experience...</p>
          </div>
        </div>
      )}
      
      <Header />
      
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-layout">
            <div className="hero-content">
              <h1 className="hero-title">Premium Class 7 Success Plan for Only $1.99</h1>
              <p className="hero-subtitle">
                Practice smarter, pass faster, and pay safely with PayPal. You can check out using your PayPal account or a credit/debit card in a few simple steps.
              </p>
              <div className="hero-cta">
                <button className="cta-primary" onClick={() => navigate('/practice')}>
                  Start Free Practice Test
                </button>
                <button className="cta-secondary" onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}>
                  Unlock Premium for $1.99
                </button>
              </div>
              <p className="hero-payment-note">Secure checkout powered by PayPal • Pay with PayPal account or card</p>
            </div>
            <div className="hero-visual" aria-hidden="true">
              <div className="hero-image-wrapper">
                <img
                  src={heroImageUrl}
                  alt="Alberta Class 7 driving test preparation"
                  className="hero-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="trust-strip" aria-label="Trust and security highlights">
        <div className="container">
          <div className="trust-grid">
            <div className="trust-item">
              <span className="trust-icon">🔐</span>
              <div>
                <h3>Secure PayPal Checkout</h3>
                <p>Your payment details are processed on PayPal's protected platform.</p>
              </div>
            </div>
            <div className="trust-item">
              <span className="trust-icon">💳</span>
              <div>
                <h3>Pay Your Way</h3>
                <p>Use PayPal balance, credit card, or debit card at checkout.</p>
              </div>
            </div>
            <div className="trust-item">
              <span className="trust-icon">⭐</span>
              <div>
                <h3>One-Time $1.99</h3>
                <p>No recurring fee. One payment unlocks premium access.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="success-section">
        <div className="container">
          <h2 className="section-title">Success Stories</h2>
          <p className="section-subtitle">Real students, real results from across Alberta</p>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-image-wrapper">
                <img 
                  src={`${process.env.PUBLIC_URL || ''}/assets/review1.png`} 
                  alt="Sarah M." 
                  className="testimonial-image"
                />
              </div>
              <div className="testimonial-quote">"I passed on my first attempt! The practice questions were exactly like the real test. The explanations helped me understand the rules, not just memorize answers."</div>
              <div className="testimonial-author">
                <div className="author-name">Sarah M.</div>
                <div className="author-location">Calgary, AB</div>
              </div>
              <div className="testimonial-badge">Passed First Try ✓</div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-image-wrapper">
                <img 
                  src={`${process.env.PUBLIC_URL || ''}/assets/review2.png`} 
                  alt="David L." 
                  className="testimonial-image"
                />
              </div>
              <div className="testimonial-quote">"John AI assistant was amazing! It answered all my questions about confusing road signs and traffic rules. Worth every penny of the premium subscription."</div>
              <div className="testimonial-author">
                <div className="author-name">David L.</div>
                <div className="author-location">Edmonton, AB</div>
              </div>
              <div className="testimonial-badge">Passed First Try ✓</div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-image-wrapper">
                <img 
                  src={`${process.env.PUBLIC_URL || ''}/assets/review3.png`} 
                  alt="Priya K." 
                  className="testimonial-image"
                />
              </div>
              <div className="testimonial-quote">"The mirror questions feature gave me confidence. I knew I was practicing with the same questions from the official Alberta test bank!"</div>
              <div className="testimonial-author">
                <div className="author-name">Priya K.</div>
                <div className="author-location">Red Deer, AB</div>
              </div>
              <div className="testimonial-badge">Passed First Try ✓</div>
            </div>
          </div>
        </div>
      </section>

      {/* Learning Process Steps */}
      <section className="learning-steps-section">
        <div className="container">
          <h2 className="section-title">🎯 Your Class 7 Learning Journey</h2>
          <p className="section-subtitle">Follow these 5 steps to pass your driving test with confidence</p>
          
          <div className="steps-timeline">
            <div className="step-item">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Download the Official Handbook</h3>
                <p>Start by reviewing the official BC/Alberta Driver's Handbook to understand the fundamentals of road safety and traffic laws.</p>
                <a 
                  href={`${process.env.PUBLIC_URL || ''}/assets/tran-drivers-guide-2023-04.pdf`}
                  download="tran-drivers-guide-2023-04.pdf"
                  className="step-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  📥 Download Official Guide
                </a>
              </div>
              <div className="step-icon">📖</div>
            </div>

            <div className="step-connector"></div>

            <div className="step-item">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Explore Structured Lessons</h3>
                <p>Work through our 39 comprehensive lessons organized across 9 critical driving topics with clear explanations and real-world examples.</p>
                <button 
                  className="step-link-btn"
                  onClick={() => {
                    if (user) {
                      navigate('/learning');
                    } else {
                      navigate('/login');
                    }
                  }}
                >
                  📚 Start Learning Path
                </button>
              </div>
              <div className="step-icon">🎓</div>
            </div>

            <div className="step-connector"></div>

            <div className="step-item">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Practice with Mirror Questions</h3>
                <p>Test yourself with 500+ mirror questions that match the format and difficulty of the official Class 7 exam.</p>
                <button 
                  className="step-link-btn"
                  onClick={() => navigate('/practice')}
                >
                  ✏️ Start Practice Questions
                </button>
              </div>
              <div className="step-icon">📝</div>
            </div>

            <div className="step-connector"></div>

            <div className="step-item">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Take Full Mock Tests</h3>
                <p>Build confidence by taking complete mock tests under timed conditions, just like the real registry exam.</p>
                <button 
                  className="step-link-btn"
                  onClick={() => navigate('/mock-test')}
                >
                  🎯 Take Mock Test
                </button>
              </div>
              <div className="step-icon">🧪</div>
            </div>

            <div className="step-connector"></div>

            <div className="step-item">
              <div className="step-number">5</div>
              <div className="step-content">
                <h3>Get Premium Assistance</h3>
                <p>Unlock John AI Assistant, detailed explanations, and unlimited questions with our CAD 1.99 Premium plan for guaranteed success.</p>
                <button 
                  className="step-link-btn premium"
                  onClick={() => navigate('/checkout')}
                >
                  ⭐ Upgrade to Premium
                </button>
              </div>
              <div className="step-icon">🚀</div>
            </div>
          </div>
        </div>
      </section>

      {/* Learning Sections Preview */}
      <section className="learning-preview-section">
        <div className="container">
          <h2 className="section-title">📚 Structured Learning Paths</h2>
          <p className="section-subtitle">Master driving knowledge with focused sections and visual learning</p>
          <div className="learning-preview-grid">
            <button 
              className="learning-preview-card"
              onClick={() => user ? navigate('/learning') : navigate('/login')}
              title={user ? 'Click to explore this lesson' : 'Login to access this lesson'}
            >
              <div className="preview-icon">🚦</div>
              <h3>Traffic Control</h3>
              <p>Learn signals, signs, and lane markings</p>
            </button>
            <button 
              className="learning-preview-card"
              onClick={() => user ? navigate('/learning') : navigate('/login')}
              title={user ? 'Click to explore this lesson' : 'Login to access this lesson'}
            >
              <div className="preview-icon">🚗</div>
              <h3>Basics of Driving</h3>
              <p>Master vehicle setup and speed control</p>
            </button>
            <button 
              className="learning-preview-card"
              onClick={() => user ? navigate('/learning') : navigate('/login')}
              title={user ? 'Click to explore this lesson' : 'Login to access this lesson'}
            >
              <div className="preview-icon">🛣️</div>
              <h3>Highways & Freeways</h3>
              <p>Navigate merges and high-speed driving</p>
            </button>
            <button 
              className="learning-preview-card"
              onClick={() => user ? navigate('/learning') : navigate('/login')}
              title={user ? 'Click to explore this lesson' : 'Login to access this lesson'}
            >
              <div className="preview-icon">🔄</div>
              <h3>Intersections & Turns</h3>
              <p>Perfect your turning and right-of-way skills</p>
            </button>
            <button 
              className="learning-preview-card"
              onClick={() => user ? navigate('/learning') : navigate('/login')}
              title={user ? 'Click to explore this lesson' : 'Login to access this lesson'}
            >
              <div className="preview-icon">⚠️</div>
              <h3>Emergency Situations</h3>
              <p>Handle challenging weather and vehicle failure</p>
            </button>
            <button 
              className="learning-preview-card"
              onClick={() => user ? navigate('/learning') : navigate('/login')}
              title={user ? 'Click to explore this lesson' : 'Login to access this lesson'}
            >
              <div className="preview-icon">😊</div>
              <h3>Responsible Driving</h3>
              <p>Build habits for legal and safe driving</p>
            </button>
            <button 
              className="learning-preview-card"
              onClick={() => user ? navigate('/learning') : navigate('/login')}
              title={user ? 'Click to explore this lesson' : 'Login to access this lesson'}
            >
              <div className="preview-icon">👥</div>
              <h3>Sharing the Road</h3>
              <p>Respect cyclists, trucks, and pedestrians</p>
            </button>
            <button 
              className="learning-preview-card"
              onClick={() => user ? navigate('/learning') : navigate('/login')}
              title={user ? 'Click to explore this lesson' : 'Login to access this lesson'}
            >
              <div className="preview-icon">⚖️</div>
              <h3>Driving with the Law</h3>
              <p>Know your legal obligations and paperwork</p>
            </button>
            <button 
              className="learning-preview-card"
              onClick={() => user ? navigate('/learning') : navigate('/login')}
              title={user ? 'Click to explore this lesson' : 'Login to access this lesson'}
            >
              <div className="preview-icon">🚚</div>
              <h3>Towing a Trailer</h3>
              <p>Master weight, braking, and sway control</p>
            </button>
          </div>
          <div className="learning-preview-cta">
            <button 
              className="cta-primary" 
              onClick={() => {
                if (user) {
                  navigate('/learning');
                } else {
                  navigate('/login');
                }
              }}
            >
              {user ? 'Start Learning Now' : 'Login to Start Learning'}
            </button>
            <p className="learning-note">9 sections • 5 questions each • Track your progress</p>
          </div>
        </div>
      </section>

      {/* Mirror Questions Section */}
      <section className="mirror-section">
        <div className="container">
          <div className="mirror-content">
            <div className="mirror-text">
              <h2 className="section-title">Official Mirror Questions from Alberta</h2>
              <p className="mirror-description">
                Our question bank contains <strong>mirror questions</strong> - questions that closely reflect 
                the official Alberta Class 7 knowledge test questions approved by the province. These questions 
                are carefully crafted to match the format, difficulty, and content areas of the real exam.
              </p>
              <ul className="mirror-features">
                <li>✓ Based on official Alberta Driver's Handbook</li>
                <li>✓ Covers all test topics: road signs, traffic laws, safe driving practices</li>
                <li>✓ Updated monthly to reflect current regulations</li>
                <li>✓ Same format as the actual registry test</li>
                <li>✓ Verified by licensed driving instructors</li>
              </ul>
            </div>
            <div className="mirror-visual">
              <div className="mirror-badge">
                <div className="badge-icon">🎯</div>
                <div className="badge-text">Official-Style Questions</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Latest News Section */}
      <section className="landing-news-section" id="latest-news">
        <div className="container">
          <div className="landing-news-header">
            <div>
              <h2 className="section-title">Latest Driving News & Updates</h2>
              <p className="section-subtitle">
                Fresh articles, updates, and practical road knowledge to help you pass with confidence.
              </p>
            </div>
          </div>

          {isNewsLoading && <p className="landing-news-state">Loading latest articles...</p>}
          {!isNewsLoading && newsError && <p className="landing-news-state error">{newsError}</p>}

          {!isNewsLoading && !newsError && latestNews.length === 0 && (
            <p className="landing-news-state">No news published yet. Please check back soon.</p>
          )}

          {!isNewsLoading && !newsError && latestNews.length > 0 && (
            <div className="landing-news-grid">
              {latestNews.map((article, index) => (
                <Link key={article.id} to={`/news/${article.slug}`} className={`landing-news-card ${index === 0 ? 'featured' : ''}`}>
                <article>
                  <img
                    src={article.img || `${process.env.PUBLIC_URL || ''}/assets/banner.png`}
                    alt={article.title}
                    className="landing-news-image"
                    loading="lazy"
                  />

                  <div className="landing-news-content">
                    <div className="landing-news-meta">
                      <span>{article.category_name || 'Driving News'}</span>
                      <span>{formatNewsDate(article.created_at)}</span>
                    </div>
                    <h3>{article.title}</h3>
                    <p>{buildExcerpt(article.content)}</p>
                    <span className="landing-news-readmore">Read full article</span>
                  </div>
                </article>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Mobile App Section */}
      <section className="mobile-app-section" aria-label="Download mobile application">
        <div className="container">
          <div className="mobile-app-card">
            <div className="mobile-app-content">
              <h2 className="section-title">Get the Mobile App</h2>
              <p className="section-subtitle">
                Study anywhere with our Alberta Class 7 prep app. Scan the QR code or tap Google Play to download now.
              </p>
              <a
                href={googlePlayAppUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="google-play-link"
                aria-label="Download on Google Play"
              >
                <img
                  src={googlePlayBadgeUrl}
                  alt="Get it on Google Play"
                  className="google-play-badge"
                />
              </a>
            </div>
            <div className="mobile-app-qr">
              <img src={qrCodeUrl} alt="QR code to download Alberta Class 7 app" className="mobile-app-qr-image" />
              <p className="mobile-app-qr-text">Scan to install on Android</p>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Features Section */}
      <section className="features-section" id="features">
        <div className="container">
          <h2 className="section-title">Premium Features</h2>
          <p className="section-subtitle">Built to match your premium plan benefits</p>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🧠</div>
              <h3 className="feature-title">200+ Alberta-Style Questions</h3>
              <p className="feature-description">
                Train with over 200 mirror-style practice questions designed for the Alberta Class 7 exam format.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🏆</div>
              <h3 className="feature-title">Pass Guarantee</h3>
              <p className="feature-description">
                Study with confidence using a premium plan built to maximize your chance of passing.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">♾️</div>
              <h3 className="feature-title">Unlimited Access</h3>
              <p className="feature-description">
                Practice anytime without limits and revisit all premium content whenever you need.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🤖</div>
              <h3 className="feature-title">AI Question Challenge Bank</h3>
              <p className="feature-description">
                Boost your readiness with AI-generated challenge questions for deeper test preparation.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔄</div>
              <h3 className="feature-title">Updated Monthly</h3>
              <p className="feature-description">
                Content is refreshed regularly to stay aligned with current Alberta driving knowledge standards.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📈</div>
              <h3 className="feature-title">Instant Question Tracking</h3>
              <p className="feature-description">
                Track your answers immediately and see where you are improving in real time.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔓</div>
              <h3 className="feature-title">One-Time Activation, Use Forever</h3>
              <p className="feature-description">
                Pay once and keep premium access for life, with no recurring subscription fees.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="payment-guide-section" id="payment-guide">
        <div className="container">
          <h2 className="section-title">How to Pay Premium in 4 Easy Steps</h2>
          <p className="section-subtitle">Simple and secure checkout with PayPal or card</p>
          <div className="payment-guide-grid">
            <article className="payment-step-card">
              <span className="payment-step-number">1</span>
              <h3>Select Premium Plan</h3>
              <p>Click any Premium button to continue to our secure checkout.</p>
            </article>
            <article className="payment-step-card">
              <span className="payment-step-number">2</span>
              <h3>Choose PayPal Checkout</h3>
              <p>On the payment page, choose to pay with your PayPal account or use a card.</p>
            </article>
            <article className="payment-step-card">
              <span className="payment-step-number">3</span>
              <h3>Complete Payment</h3>
              <p>Confirm your one-time $1.99 payment. No hidden or recurring charges.</p>
            </article>
            <article className="payment-step-card">
              <span className="payment-step-number">4</span>
              <h3>Get Instant Access</h3>
              <p>Your premium features activate right away so you can keep studying.</p>
            </article>
          </div>
          <div className="payment-support-box">
            <h3>Need help during checkout?</h3>
            <p>If payment does not go through, contact support and we will guide you step by step.</p>
            <div className="payment-support-actions">
              <a href="mailto:mailtam62@gmail.com" className="payment-support-link">Email Support</a>
              <a href="tel:+18252880437" className="payment-support-link">Call Support</a>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section" id="pricing">
        <div className="container">
          <h2 className="section-title">Simple, Affordable Pricing</h2>
          <p className="section-subtitle">One-time payment with PayPal account or credit/debit card</p>
          <div className="pricing-cards">
            <div className="pricing-card free">
              <div className="pricing-label">Free</div>
              <div className="pricing-price">$0</div>
              <div className="pricing-duration">Forever</div>
              <ul className="pricing-features">
                <li>✓ 50 practice questions</li>
                <li>✓ Basic explanations</li>
                <li>✓ Progress tracking</li>
                <li>✗ John AI Assistant</li>
                <li>✗ Full question bank</li>
                <li>✗ Detailed explanations</li>
              </ul>
              <button className="pricing-btn" onClick={() => navigate('/mock-test')}>
                Start Free
              </button>
            </div>
            <div className="pricing-card premium">
              <div className="pricing-badge">Most Popular</div>
              <div className="pricing-label">Premium</div>
              <div className="pricing-price">$1.99</div>
              <div className="pricing-duration">Unlimited Access</div>
              <ul className="pricing-features">
                <li>✓ 200+ Alberta-style practice questions</li>
                <li>✓ Pass guarantee</li>
                <li>✓ Unlimited access</li>
                <li>✓ AI question challenge bank</li>
                <li>✓ Updated monthly</li>
                <li>✓ Instant question tracking</li>
                <li>✓ One-time activation, use forever</li>
              </ul>
              <button className="pricing-btn premium-btn" onClick={() => navigate('/checkout')}>
                🚀 Pay $1.99 Securely with PayPal
              </button>
              <p className="pricing-subtext">One-time payment • No recurring charge • Instant activation</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <p className="section-subtitle">Everything you need to know about Alberta Class 7</p>
          <div className="faq-list">
            {faqData.map((faq, index) => (
              <div className="faq-item" key={index}>
                <div className="faq-question" onClick={() => toggleFAQ(index)}>
                  <span>{faq.question}</span>
                  <span className={`faq-icon ${openFAQ === index ? 'open' : ''}`}>▼</span>
                </div>
                {openFAQ === index && (
                  <div className="faq-answer">{faq.answer}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="container">
          <h2 className="section-title">Get in Touch</h2>
          <p className="section-subtitle">Have questions? We're here to help!</p>
          <div className="contact-grid">
            <div className="contact-card">
              <div className="contact-icon">📧</div>
              <h3 className="contact-title">Email Us</h3>
              <a href="mailto:mailtam62@gmail.com" className="contact-link">
                mailtam62@gmail.com
              </a>
              <p className="contact-note">We respond within 24 hours</p>
            </div>
            <div className="contact-card">
              <div className="contact-icon">📞</div>
              <h3 className="contact-title">Call Us</h3>
              <a href="tel:+18252880437" className="contact-link">
                (825) 288 - 0437 
              </a>
              <p className="contact-note">Mon-Fri: 9AM - 6PM MST</p>
            </div>
            <div className="contact-card">
              <a href='https://www.facebook.com/john.nguyen.423776/' style={{ textDecoration: 'none'}} target="_blank" rel="noopener noreferrer" aria-label="Chat with John on Facebook Messenger">
              <div className="contact-icon">💬</div></a>
              <h3 className="contact-title">Live Chat</h3>
              <div className="contact-link">Chat with John AI</div>
              <p className="contact-note">Available 24/7 for premium users</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-info">
              <h3 className="footer-title">Alberta Class 7 Test Prep</h3>
              <p className="footer-text">
                Your trusted partner for passing the Alberta Class 7 knowledge test. 
                All content based on the official Alberta Driver's Handbook.
              </p>
            </div>
            <div className="footer-links">
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
              <a href="mailto:support@albertaclass7.com">Contact</a>
              <a href="#privacy">Privacy Policy</a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 Alberta Class 7 Test Prep. Not affiliated with the Government of Alberta.</p>
          </div>
        </div>
      </footer>

      {/* Facebook Messenger Icon Button */}
      <a
        href="https://m.me/john.nguyen.423776"
        target="_blank"
        rel="noopener noreferrer"
        className="messenger-button"
        aria-label="Chat with John on Facebook Messenger"
        title="Chat with John on Facebook Messenger"
      >
        <svg className="messenger-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12c0 4.84 3.46 8.87 8 9.8V15h-2v-2h2v-1.5c0-2.04 1.21-3.17 3.08-3.17.87 0 1.8.15 1.8.15v1.98h-1.01c-1 0-1.31.62-1.31 1.26V13h2.23l-.35 2h-1.88v6.8c4.54-.93 8-4.96 8-9.8 0-5.52-4.48-10-10-10z"/>
        </svg>
        <span className="messenger-label">Chat on Messenger</span>
      </a>
    </div>
  );
};

// FAQ Data
const faqData = [
  {
    question: "What is the Alberta Class 7 driver's licence?",
    answer: "The Class 7 licence is a learner's permit that allows new drivers in Alberta to practice driving under supervision. It's the first step in the Graduated Driver Licensing (GDL) program. To get it, you must pass a written knowledge test covering traffic laws, road signs, and safe driving practices."
  },
  {
    question: "How many questions are on the Class 7 knowledge test?",
    answer: "The Alberta Class 7 knowledge test consists of 30 multiple-choice questions. You need to answer at least 25 questions correctly (83%) to pass. The test is divided into two parts: road signs and rules of the road."
  },
  {
    question: "How long do I need to wait to retake the test if I fail?",
    answer: "If you fail the Class 7 knowledge test, you must wait at least one day before you can retake it. There is a fee for each test attempt. We recommend studying with our practice tests to improve your chances of passing on your next try."
  },
  {
    question: "Are your practice questions the same as the real test?",
    answer: "Our questions are 'mirror questions' - they closely reflect the official test questions in format, difficulty, and content. While we cannot use the exact questions from the registry test, our questions are based on the same official Alberta Driver's Handbook and cover all the same topics you'll see on the real exam."
  },
  {
    question: "What topics are covered in the Class 7 test?",
    answer: "The test covers traffic signs, traffic lights, right-of-way rules, speed limits, safe driving practices, impaired driving laws, demerit points, parking rules, and emergency vehicle procedures. All topics are from the Alberta Driver's Handbook."
  },
  {
    question: "How is the premium subscription different from free?",
    answer: "Premium gives you access to 500+ questions (vs 50 free), detailed explanations for every answer, the John AI assistant for instant help, monthly content updates, progress tracking across all topics, and priority customer support - all for just $1.99 CAD with unlimited access."
  },
  {
    question: "Can I cancel my premium subscription?",
    answer: "Our premium plan is a one-time payment of $1.99 CAD for unlimited lifetime access - there's no recurring subscription to cancel. Once you purchase, you have access forever, including all future updates."
  },
  {
    question: "Can I pay with a credit card if I do not have a PayPal account?",
    answer: "Yes. At checkout, select PayPal and then choose the card option. PayPal supports guest checkout in many regions so you can pay by credit or debit card without creating a PayPal account."
  },
  {
    question: "Is my payment information safe?",
    answer: "Yes. Payments are processed through PayPal's secure infrastructure. We do not store your full card details on our servers."
  },
  {
    question: "What is the John AI assistant?",
    answer: "John is an AI-powered assistant available to premium users that can answer your specific questions about Alberta driving rules, explain confusing road signs, clarify traffic laws, and provide personalized study tips. Available 24/7 to help you prepare for your test."
  }
];

export default LandingPage;
